import { FC, useMemo } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';

import CourseApproval from 'components/Auth/CourseApproval';
import ChildCourseSelector from 'components/Course/ChildCourseSelector';
import SelectCourse from 'components/Course/SelectCourse';
import AuthModule from 'containers/AuthModule';
import CourseDashboard from 'containers/CourseDashboard';
import ImportCourse from 'containers/ImportCourse';
import Logout from 'containers/Logout';
import MessagePage from 'containers/MessagePage';
import ProtectedRoute from 'ProtectedRoute';
import { RootState } from 'redux/reducers';
import { ROUTES } from 'routes';

import css from './App.module.scss';
import Header from './components/Header';
import AdminDashboard from './containers/AdminDashboard';
import Main from './containers/Main';

interface AppProps {
  isAppLoading: boolean;
  getAccess: SessionData | null;
  selectedParentCourseId: string | null;
  selectedChildCourseId: string | null;
}

const App: FC<AppProps> = (props) => {
  return (
    <div className='app'>
      <Header
        title={
          <HeaderTitle getAccess={props.getAccess} selectedChildCourseId={props.selectedChildCourseId} />
        }
      />
      <div className='app__content'>
        <Switch>
          <Route path={[ROUTES.LTI_LAUNCH_REDIR, ROUTES.AUTH_REDIRECT]} component={Main} />
          <Route path={ROUTES.AUTH} component={AuthModule} />
          <Route path={ROUTES.LOG_OUT} component={Logout} />
          <Route path={ROUTES.COURSE_APPROVAL} component={CourseApproval} />
          <ProtectedRoute
            forContext='course'
            path={ROUTES.COURSES}
            component={CourseDashboard}
            identifier={`${props.selectedParentCourseId} + ${props.selectedChildCourseId}`}
          />
          <ProtectedRoute
            forContext='course'
            path={ROUTES.COURSES_CREATE}
            exact
            component={ImportCourse}
            identifier={`${props.selectedParentCourseId} + ${props.selectedChildCourseId}`}
          />
          <ProtectedRoute forContext='course' path={ROUTES.COURSES_SELECT_CHILD} component={SelectCourse} />
          <ProtectedRoute forContext='organization' path={ROUTES.ADMIN} component={AdminDashboard} />
          <Route path={ROUTES.MESSAGE_PAGE} component={MessagePage} />
          <Redirect
            to={{ pathname: ROUTES.MESSAGE_PAGE, state: { code: '404', message: 'Page not found' } }}
          />
        </Switch>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    isAppLoading: state.isAppLoading,
    getAccess: state.getAccess,
    selectedParentCourseId: state.selectedParentCourseId,
    selectedChildCourseId: state.selectedChildCourseId,
  };
};

export default connect(mapStateToProps)(App);

interface HeaderTitleProps {
  getAccess: SessionData | null;
  selectedChildCourseId: string | null;
}

export const HeaderTitle: FC<HeaderTitleProps> = ({ getAccess, selectedChildCourseId }) => {
  const { code, title } = useMemo(() => {
    if (!getAccess) return {};
    if (getAccess.context !== 'course') return {};

    if (selectedChildCourseId) {
      const childCourse = getAccess.courseDetails.childCourses.find(
        (childCourse) => childCourse.childId === selectedChildCourseId
      );

      return { code: childCourse!.code, title: childCourse!.title };
    }

    return { code: getAccess.courseDetails.code, title: getAccess.courseDetails.title };
  }, [getAccess, selectedChildCourseId]);

  if (!getAccess) return null;

  return (
    <div>
      {getAccess.context === 'course' ? (
        <div className={css['course']}>
          <div className={css['course-details']}>
            <div className={css['course-code']}>{code}</div>
            <div className={css['course-title']}>{title}</div>
          </div>
          <ChildCourseSelector />
        </div>
      ) : (
        <div>{getAccess.organizationDetails.name}</div>
      )}
    </div>
  );
};
